// @flow
import React from 'react';
import styled from 'styled-components';
import Box from 'components/Box';
import Parallax, { PARALLAX_DEFAULT_SPEED } from 'containers/Parallax';
import {
  RichTextHeadline,
  RichText,
  BodyLarge,
  BodySmall,
  HeadingSmall
} from 'components/Text';
import ShowModalContainer from 'containers/ShowModalContainer';
import ButtonLink from 'components/Cms/ButtonLink';
import Button from 'components/Button';
import Video, { PlayButton } from 'components/Video';
import { FilestackImage, getSrc } from 'components/Image';
import type {
  SplitContentBlockData,
  SplitContentSection,
  SplitBlockAppearance
} from 'api/cms';
import { hiDPI } from 'utils/style';
import { colors, gutterWidth, bp } from '../../theme';

const { useEffect, useState } = React;

const Blockquote = styled(Box)`
  &::before {
    content: '\\201c';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -35%);
    color: ${colors.marigold};
    font-size: 100px;
    font-weight: 700;
    line-height: 1;
  }
`;

const ImageContainer = styled(
  ({ handle, format, fixedWidth, fullWidth, overlapping, ...props }) => (
    <Box {...props} />
  )
)`
  width: 100%;
  height: 100%;
  background-position: center;
  ${props => `
    background-size: ${props.fixedWidth ? 'contain' : 'cover'};
    background-image: url('${getSrc({
      handle: props.handle,
      width: props.fullWidth ? 996 : props.overlapping ? 696 : 552,
      format: props.format,
      mimetype: props.mimetype
    })}');
    ${hiDPI()} {
      background-image: url('${getSrc({
        handle: props.handle,
        width: props.fullWidth ? 1992 : props.overlapping ? 1392 : 1104,
        format: props.format,
        mimetype: props.mimetype
      })}');
    }
    ${
      !props.fixedWidth
        ? `
      * {
        width: 100%;
        height: 100%;
      }
      svg {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 40px;
        height: 40px;
      }
      img {
        width: 100%;
        height: auto;
        display: none;
      }
      @supports (object-fit: cover) {
        background-image: none;
        img {
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
        }
      }
    `
        : `
      img {
        width: 100%;
      }
      @supports (object-fit: contain) {
        background-image: none;
        img {
          height: 100%;
          object-fit: contain;
          object-position: center;
          display: block;
        }
      }
        `
    }
  `}
`;

const Content = ({
  section,
  fullWidth,
  appearance,
  index,
  blockId,
  showTextBox,
  button,
  blockIndex,
  pageUpdated
}: {
  section: SplitContentSection,
  fullWidth: boolean,
  appearance: SplitBlockAppearance,
  index: number,
  blockId: string,
  showTextBox: boolean,
  button?: { title: string, href: string },
  blockIndex: number,
  pageUpdated: string
}) => {
  const overlapping = appearance === 'OVERLAPPING';
  switch (section.type) {
    case 'TEXT': {
      return (
        <Box
          position="relative"
          px={[gutterWidth / 2, null, gutterWidth]}
          pr={showTextBox || [null, null, 0]}
          py={[
            gutterWidth / 2,
            null,
            ['OVERLAPPING', 'FULL_WIDTH'].includes(appearance) ? gutterWidth : 0
          ]}
          height="100%"
        >
          {section.data.label && (
            <HeadingSmall color={colors.battleshipGrey}>
              {section.data.label}
            </HeadingSmall>
          )}
          {section.data.headline && (
            <Box
              display="block"
              mb={24}
              color={colors.greyishBrown}
              textDecoration="none"
            >
              <RichTextHeadline>{section.data.headline}</RichTextHeadline>
            </Box>
          )}
          <RichText
            analyticsData={{
              eventName: 'CMS Block',
              eventProperties: {
                block_position: blockIndex,
                block_type: 'Split Content'
              }
            }}
            dangerouslySetInnerHTML={{ __html: section.data.html }}
            styleAs="body"
            tag="div"
          />
          {section.data.button && (
            <ButtonLink
              sectionButton={section.data.button}
              eventProperties={{
                block_position: blockIndex,
                block_type: 'Split Content',
                cta_type: 'Button',
                button_text: section.data.button.text,
                page_updated: pageUpdated
              }}
              blockId={blockId}
              index={index}
            />
          )}
        </Box>
      );
    }
    case 'BLOCKQUOTE':
      return (
        <Blockquote
          component="blockquote"
          position="relative"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          height="100%"
          m={0}
          p={[gutterWidth / 2, null, gutterWidth]}
        >
          <BodyLarge
            analyticsData={{
              eventName: 'CMS Block',
              eventProperties: {
                block_position: blockIndex,
                block_type: 'Split Content'
              }
            }}
            dangerouslySetInnerHTML={{ __html: section.data.quote }}
          />
          {section.data.cite && <BodySmall>{section.data.cite}</BodySmall>}
        </Blockquote>
      );
    case 'VIDEO':
      return (
        <ShowModalContainer
          render={showModal => (
            <Box
              component={Button}
              position="relative"
              width="100%"
              height="100%"
              onClick={() => {
                showModal('MEDIA_GALLERY', {
                  items: [{ type: 'VIDEO', videoUrl: section.data.url }],
                  itemIndex: 0
                });
              }}
              aria-label={getText('Open video')}
            >
              <Video
                minHeight={['50vw', null, '25vw', (540 * 9) / 16]}
                url={section.data.url}
                prefix="split-block-video-"
                fill
                noPlayer
                hideButton={fullWidth}
              />
            </Box>
          )}
        />
      );
    case 'IMAGE': {
      const format = !['image/gif', 'image/jpeg', 'image/png'].includes(
        section.data.image.mimetype
      )
        ? 'jpg'
        : undefined;
      return (
        <ImageContainer
          handle={section.data.image && section.data.image.handle}
          mimetype={section.data.image && section.data.image.mimetype}
          format={format}
          fixedWidth={section.data.size === 'FIXED_WIDTH'}
          fullWidth={fullWidth}
          overlapping={overlapping}
          component={button ? 'a' : 'div'}
          href={button ? button.href : null}
        >
          <FilestackImage
            data={section.data.image}
            alt={
              (section.data.image && section.data.image.altText) ||
              (section.data.image && section.data.image.filename)
            }
            width={fullWidth ? 1440 : overlapping ? 696 : 552}
            format={format}
          />
        </ImageContainer>
      );
    }

    default:
      return null;
  }
};

const SplitContentBlock = ({
  blockId,
  data: { sections, appearance },
  fullWidthLayout,
  parallax,
  blockIndex,
  pageUpdated,
  preview
}: {
  blockId: string,
  data: SplitContentBlockData,
  fullWidthLayout: boolean,
  parallax: boolean,
  blockIndex: number,
  pageUpdated: string,
  preview?: boolean
}) => {
  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    const resizeHandler = () =>
      setIsDesktop(window && window.innerWidth >= bp[1]);
    window.addEventListener('resize', resizeHandler);
    resizeHandler();
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, []);

  const actualAppearance =
    fullWidthLayout &&
    sections.some(
      section => section.type === 'IMAGE' || section.type === 'VIDEO'
    ) &&
    sections.some(
      section => section.type === 'TEXT' || section.type === 'BLOCKQUOTE'
    )
      ? appearance
      : 'SIDE_BY_SIDE';

  const flipOnMobile =
    ['TEXT', 'BLOCKQUOTE'].includes(sections[0].type) &&
    ['IMAGE', 'VIDEO'].includes(sections[1].type);

  return (
    <Box py={actualAppearance !== 'SIDE_BY_SIDE' && gutterWidth}>
      <Box
        position="relative"
        display="flex"
        flexDirection={[
          flipOnMobile ? 'column-reverse' : 'column',
          null,
          'row'
        ]}
        alignItems={actualAppearance === 'FULL_WIDTH' && 'center'}
        m={[-gutterWidth / 4, null, -gutterWidth / 2]}
        width={[
          `calc(100% + ${gutterWidth / 2}px)`,
          null,
          `calc(100% + ${gutterWidth}px)`
        ]}
        minHeight={actualAppearance === 'FULL_WIDTH' && 'calc(100vh - 300px)'}
      >
        {sections.map((section, index) => {
          const overlapping =
            actualAppearance === 'OVERLAPPING' &&
            (section.type === 'IMAGE' || section.type === 'VIDEO');
          const fullWidth =
            actualAppearance === 'FULL_WIDTH' &&
            (section.type === 'IMAGE' || section.type === 'VIDEO') &&
            isDesktop;
          const showTextBox =
            ['FULL_WIDTH', 'OVERLAPPING'].includes(actualAppearance) &&
            ['TEXT', 'BLOCKQUOTE'].includes(section.type);
          const withParallax = parallax && fullWidth;
          return (
            <Box
              position={!fullWidth && 'relative'}
              zIndex={showTextBox && 1}
              width={[1, null, 1 / 2]}
              p={[gutterWidth / 4, null, gutterWidth / 2]}
              // eslint-disable-next-line react/no-array-index-key
              key={`${section.type}-${index}`}
            >
              <Box
                position={[null, null, fullWidth && !preview && 'absolute']}
                left={[null, null, '50%']}
                top={[null, null, '50%']}
                transform={[
                  null,
                  null,
                  fullWidth && !preview && 'translate(-50%, -50%)'
                ]}
                height={
                  section.type === 'IMAGE' &&
                  section.data.size === 'FIXED_WIDTH'
                    ? 'auto'
                    : [
                        '100%',
                        null,
                        overlapping || fullWidth
                          ? `calc(100% + ${
                              overlapping ? gutterWidth * 2 : gutterWidth
                            }px)`
                          : '100%'
                      ]
                }
                mt={overlapping && [0, null, -gutterWidth]}
                mb={overlapping && [-gutterWidth, null, 0]}
                width={
                  overlapping
                    ? [1, null, `calc(100% + ${gutterWidth * 3}px)`]
                    : fullWidth
                    ? [1, null, '100vw']
                    : 1
                }
                ml={[0, null, overlapping && index === 1 && -gutterWidth * 3]}
                overflow={
                  ['TEXT', 'BLOCKQUOTE'].includes(section.type)
                    ? undefined
                    : 'hidden'
                }
                bg={
                  section.type === 'TEXT' || showTextBox ? 'white' : colors.fog
                }
                boxShadow={showTextBox && '0 0 6px rgba(0,0,0,0.3)'}
                borderRadius={[showTextBox ? 12 : 24, null, fullWidth && 0]}
                style={
                  section.type === 'IMAGE' && {
                    '-webkit-mask-image':
                      '-webkit-radial-gradient(white, black)'
                  }
                }
              >
                <>
                  <Parallax
                    disabled={!withParallax}
                    position={fullWidth && 'absolute'}
                    bottom={0}
                    left={0}
                    width="100%"
                    height={
                      withParallax
                        ? `calc(100% + ${PARALLAX_DEFAULT_SPEED}px)`
                        : '100%'
                    }
                  >
                    <Content
                      section={section}
                      showTextBox={showTextBox}
                      fullWidth={fullWidth}
                      appearance={actualAppearance}
                      index={index}
                      blockId={blockId}
                      parallax={parallax}
                      blockIndex={blockIndex}
                      pageUpdated={pageUpdated}
                    />
                  </Parallax>
                  {fullWidth && section.type === 'VIDEO' && (
                    <Box
                      position="absolute"
                      top="50%"
                      left={['50%', null, index === 0 ? '25%' : '75%']}
                      transform="translate(-50%, -50%)"
                      pointerEvents="none"
                    >
                      <PlayButton />
                    </Box>
                  )}
                </>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SplitContentBlock;
